import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { Product, CartItem, PurchaseType } from '../types'
import { clientProductApi, clientOrderApi } from '../utils/api'
import type { CreatedOrder } from '../utils/api'
import { buildOrderPayload, getOrderErrorMessage } from '../utils/orderSubmission'
import { formatRupiah, getPriceForType } from '../types'
import { ProductRow } from '../components/order/ProductRow'
import { FloatingCartButton } from '../components/order/FloatingCartButton'
import { CartDrawer } from '../components/order/CartSidebar'
import { OrderReviewModal } from '../components/order/OrderReviewModal'
import { ClientHeader } from '../components/layout/ClientHeader'
import { ClientFooter } from '../components/layout/ClientFooter'

export function OrderPage() {
  const [outletName, setOutletName] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [showReview, setShowReview] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [completedOrder, setCompletedOrder] = useState<CreatedOrder | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  // Synchronous duplicate-submission guard: blocks a second submit even
  // before React has re-rendered the disabled button.
  const submittingRef = useRef(false)

  const fetchProducts = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)
    try {
      const res = await clientProductApi.getAll()
      const data = res.data as Product[]
      setProducts(data)
    } catch {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const addToCart = (product: Product, purchaseType: PurchaseType, quantity: number) => {
    const unitPrice = getPriceForType(product, purchaseType)
    if (!unitPrice) return

    const subtotal = quantity * unitPrice
    const newItem: CartItem = {
      productId: product.id,
      productName: product.name,
      purchaseType,
      quantity,
      unitPrice,
      subtotal,
    }

    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.productId === product.id && item.purchaseType === purchaseType
      )
      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
          subtotal: (updated[existingIndex].quantity + quantity) * unitPrice,
        }
        return updated
      }
      return [...prev, newItem]
    })
  }

  const updateQuantity = (productId: number, purchaseType: PurchaseType, delta: number) => {
    setCart((prev) => {
      const itemIndex = prev.findIndex(
        (item) => item.productId === productId && item.purchaseType === purchaseType
      )
      if (itemIndex < 0) return prev
      const newQuantity = prev[itemIndex].quantity + delta
      if (newQuantity <= 0) {
        return prev.filter((item) => !(item.productId === productId && item.purchaseType === purchaseType))
      }
      const updated = [...prev]
      const unitPrice = updated[itemIndex].unitPrice
      updated[itemIndex] = {
        ...updated[itemIndex],
        quantity: newQuantity,
        subtotal: newQuantity * unitPrice,
      }
      return updated
    })
  }

  const removeFromCart = (productId: number, purchaseType: PurchaseType) => {
    setCart((prev) => prev.filter((item) => !(item.productId === productId && item.purchaseType === purchaseType)))
  }

  const clearCart = () => {
    setCart([])
  }

  const getTotal = () => cart.reduce((sum, item) => sum + item.subtotal, 0)

  // Categories come from real loaded product data — never hardcoded.
  const categories = useMemo(() => {
    const cats = new Set(
      products
        .map((p) => p.category)
        .filter((c): c is string => c !== null && c !== undefined && c.trim() !== '')
    )
    return Array.from(cats).sort((a, b) => a.localeCompare(b, 'id'))
  }, [products])

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return products.filter((product) => {
      const matchesCategory = categoryFilter === '' || product.category === categoryFilter
      if (!matchesCategory) return false
      if (query === '') return true
      return (
        product.name.toLowerCase().includes(query) ||
        (product.category ?? '').toLowerCase().includes(query)
      )
    })
  }, [products, searchQuery, categoryFilter])

  // Total cart quantity per product across ALL purchase types. Cart lines
  // themselves stay split per (product, purchaseType) — this map only feeds
  // the contextual "X item di keranjang" row indicator.
  const cartCountByProduct = useMemo(() => {
    const counts = new Map<number, number>()
    for (const item of cart) {
      counts.set(item.productId, (counts.get(item.productId) ?? 0) + item.quantity)
    }
    return counts
  }, [cart])

  const handleConfirmOrder = () => {
    if (!outletName.trim()) {
      alert('Silakan masukkan nama outlet')
      return
    }
    if (cart.length === 0) {
      alert('Keranjang kosong')
      return
    }
    setShowReview(true)
  }

  const handleEditOrder = () => {
    if (isSubmitting) return
    setShowReview(false)
    setIsCartOpen(true)
  }

  /**
   * Phase 7B-1 — submit the cart to the real POST /api/orders.
   * Sends only outlet_name + product_id/purchase_type/quantity; the backend
   * response is the source of truth (real order number, prices, totals).
   * On failure the cart and outlet name stay intact for retry.
   */
  const handleCompleteOrder = async () => {
    if (submittingRef.current) return
    if (!outletName.trim()) {
      setSubmitError('Silakan masukkan nama outlet')
      return
    }
    if (cart.length === 0) {
      setSubmitError('Keranjang kosong')
      return
    }
    submittingRef.current = true
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const res = await clientOrderApi.create(buildOrderPayload(outletName, cart))
      setCompletedOrder(res.data)
      setShowReview(false)
      setShowSuccess(true)
    } catch (err) {
      setSubmitError(getOrderErrorMessage(err))
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const handleDismissSuccess = () => {
    setShowSuccess(false)
    setCompletedOrder(null)
    setSubmitError(null)
    clearCart()
    setOutletName('')
    setIsCartOpen(false)
  }

  if (isLoading) {
    return (
      <div className="order-page min-h-screen bg-slate-50">
        <ClientHeader cartCount={0} onCartClick={() => {}} />
        <div className="flex-1 flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
        <ClientFooter />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="order-page min-h-screen bg-slate-50">
        <ClientHeader cartCount={0} onCartClick={() => {}} />
        <main className="flex-1 flex items-center justify-center">
          <div className="card p-8 text-center max-w-md error-state">
            <svg className="w-16 h-16 text-red-300 mx-auto mb-4 error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-bold text-slate-900 mb-2 error-title">Gagal Memuat Produk</h2>
            <p className="text-slate-500 mb-4 error-message">Tidak dapat terhubung ke server. Silakan coba lagi.</p>
            <button onClick={fetchProducts} className="btn-primary">
              Coba Lagi
            </button>
          </div>
        </main>
        <ClientFooter />
      </div>
    )
  }

  return (
    <div className="order-page min-h-screen bg-slate-50 flex flex-col">
      <ClientHeader cartCount={0} onCartClick={() => {}} />

      <main className="flex-1">
        <div className="container" style={{ paddingTop: '24px', paddingBottom: '100px' }}>
          <section className="outlet-section" aria-labelledby="outlet-heading">
            <div className="outlet-card">
              <div className="outlet-icon" aria-hidden="true">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="outlet-content">
                <h2 id="outlet-heading" className="outlet-title">Nama Warung / Toko</h2>
                <p className="outlet-description">
                  Masukkan nama warung atau toko Anda untuk memulai pemesanan.
                  Nama ini akan tercetak di struk pesanan.
                </p>
                <div className="outlet-input-wrapper">
                  <input
                    id="outlet-name"
                    type="text"
                    value={outletName}
                    onChange={(e) => setOutletName(e.target.value)}
                    placeholder="Contoh: Toko Maju Jaya, Warung Berkah, dll"
                    className="outlet-input"
                    maxLength={100}
                    required
                    aria-required="true"
                    aria-describedby="outlet-hint"
                  />
                  <span className="outlet-required-indicator" aria-hidden="true">*</span>
                </div>
                <p id="outlet-hint" className="outlet-hint">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Wajib diisi sebelum menambah produk ke keranjang
                </p>
              </div>
            </div>
          </section>

          <section className="products-section" aria-labelledby="products-heading">
            <div className="section-header">
              <h2 id="products-heading" className="section-title">Daftar Produk</h2>
              <span className="section-subtitle products-count">
                {searchQuery.trim() !== '' || categoryFilter !== ''
                  ? `Menampilkan ${filteredProducts.length} dari ${products.length} produk`
                  : `${products.length} produk tersedia`}
              </span>
            </div>

            <div className="order-tools">
              <div className="order-search-wrapper">
                <svg className="order-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  id="product-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cari produk..."
                  aria-label="Cari produk"
                  className="order-search-input"
                />
              </div>
              {categories.length > 0 && (
                <div className="category-chips" role="group" aria-label="Filter kategori">
                  <button
                    type="button"
                    onClick={() => setCategoryFilter('')}
                    className={`category-chip ${categoryFilter === '' ? 'active' : ''}`}
                    aria-pressed={categoryFilter === ''}
                  >
                    Semua
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setCategoryFilter(categoryFilter === category ? '' : category)}
                      className={`category-chip ${categoryFilter === category ? 'active' : ''}`}
                      aria-pressed={categoryFilter === category}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {products.length === 0 ? (
              <div className="card p-12 text-center empty-state">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4 empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="empty-text">Belum ada produk tersedia</p>
                <p className="empty-hint">Silakan login ke admin untuk menambah produk</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="card p-12 text-center empty-state">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4 empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="empty-text">Produk tidak ditemukan</p>
                <p className="empty-hint">Periksa nama produk atau pilih kategori lain.</p>
              </div>
            ) : (
              <ul className="product-list" aria-label="Daftar produk">
                {filteredProducts.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    cartCount={cartCountByProduct.get(product.id) ?? 0}
                    onAddToCart={addToCart}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      <ClientFooter />

      <FloatingCartButton count={cart.length} onClick={() => setIsCartOpen(true)} />

      {isCartOpen && (
        <CartDrawer
          cart={cart}
          total={getTotal()}
          onClose={() => setIsCartOpen(false)}
          onRemove={removeFromCart}
          onUpdateQuantity={updateQuantity}
          onCheckout={handleConfirmOrder}
          disabled={!outletName.trim()}
          outletName={outletName}
        />
      )}

      <OrderReviewModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        onEdit={handleEditOrder}
        onComplete={handleCompleteOrder}
        outletName={outletName}
        cart={cart}
        total={getTotal()}
        formatRupiah={formatRupiah}
        isSubmitting={isSubmitting}
        submitError={submitError}
      />

      {showSuccess && completedOrder && (
        <div className="modal-overlay" onClick={handleDismissSuccess} role="dialog" aria-modal="true" aria-labelledby="success-title">
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon-wrapper">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 id="success-title" className="success-title">Pesanan Berhasil</h2>
            <p className="success-message">
              Pesanan <strong>{completedOrder.orderNumber}</strong> untuk{' '}
              <strong>{completedOrder.outlet.name}</strong> telah berhasil disimpan.
            </p>
            <div className="modal-section">
              <div className="modal-total-row">
                <span className="modal-total-label">Status</span>
                <span className="modal-total-value">{completedOrder.status}</span>
              </div>
              <div className="modal-total-row">
                <span className="modal-total-label">Total ({completedOrder.items.length} item)</span>
                <span className="modal-total-value">{formatRupiah(completedOrder.total)}</span>
              </div>
            </div>
            <button onClick={handleDismissSuccess} className="btn btn-primary btn-full success-dismiss-btn">
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  )
}